import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header, SEOHead } from '../components';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  MapPinIcon,
  MagnifyingGlassIcon,
  StarIcon,
  UserGroupIcon,
  ChartBarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  CheckIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  CogIcon
} from '@heroicons/react/24/outline';

const LeadGenerationPage: React.FC = () => {
  const navigate = useNavigate();

  // SEO setup
  useSEO(pageConfigs.leadGeneration);

  // Navigation handlers
  const handleGetAudit = () => {
    navigate('/consultation');
  };

  const handleViewCaseStudies = () => {
    navigate('/case-studies');
  };

  const handleCallPhone = () => {
    window.location.href = 'tel:+919711834001';
  };

  const handleSendEmail = () => {
    window.location.href = 'mailto:info@3arrowsmedia.com';
  };
  const features = [
    {
      title: "Local SEO Optimization",
      description: "Optimize your website for local search results to appear when customers search for your services in your area.",
      icon: <MagnifyingGlassIcon className="w-8 h-8" />,
      benefits: ["Local keyword targeting", "Google My Business optimization", "Local citations management", "Review generation"]
    },
    {
      title: "Google My Business Management",
      description: "Complete management of your Google Business Profile to maximize local visibility and customer engagement.",
      icon: <BuildingOfficeIcon className="w-8 h-8" />,
      benefits: ["Profile optimization", "Review management", "Post scheduling", "Insights tracking"]
    },
    {
      title: "Local PPC Campaigns",
      description: "Targeted pay-per-click advertising campaigns focused on your local market and specific customer demographics.",
      icon: <ChartBarIcon className="w-8 h-8" />,
      benefits: ["Geographic targeting", "Local keyword bidding", "Mobile optimization", "Call tracking"]
    },
    {
      title: "Review Management",
      description: "Build and manage your online reputation through strategic review generation and management.",
      icon: <StarIcon className="w-8 h-8" />,
      benefits: ["Review generation", "Response management", "Reputation monitoring", "Crisis management"]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Local Market Analysis",
      description: "We analyze your local market, competitors, and target audience to identify the best opportunities for lead generation."
    },
    {
      step: "02",
      title: "Strategy Development",
      description: "Create a comprehensive local lead generation strategy tailored to your business and market."
    },
    {
      step: "03",
      title: "Implementation",
      description: "Execute the strategy across all local channels including SEO, PPC, and social media."
    },
    {
      step: "04",
      title: "Optimization & Growth",
      description: "Continuously monitor performance and optimize campaigns to maximize lead generation and ROI."
    }
  ];

  const caseStudy = {
    client: "Local Dental Practice",
    industry: "Healthcare",
    challenge: "Struggling to attract new patients in a competitive local market",
    solution: "Implemented comprehensive local SEO and Google My Business optimization",
    results: [
      "150+ qualified leads monthly",
      "300% increase in local search visibility",
      "85% of leads converted to patients",
      "ROI of 450% in first 6 months"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.leadGeneration} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
                <MapPinIcon className="w-5 h-5 text-blue-600" />
                <span className="text-blue-700 font-semibold text-sm">Local Business Specialists</span>
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
                Lead Generation for 
                <span className="text-blue-600"> Local Business</span>
              </h1>
              
              <p className="text-lg text-slate-700 mb-8 leading-relaxed">
                Attract qualified local customers who are actively searching for your services. 
                Our multi-channel approach ensures your business appears when and where it matters most.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={handleGetAudit}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Get Free Local Audit</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleViewCaseStudies}
                  className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg border-2 border-slate-200 transition-colors"
                >
                  View Case Studies
                </button>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">150+</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Leads Monthly</div>
                  <div className="text-xs text-slate-500">Average for clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">300%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Visibility Increase</div>
                  <div className="text-xs text-slate-500">Local search results</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">85%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Conversion Rate</div>
                  <div className="text-xs text-slate-500">Lead to customer</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">450%</div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">ROI</div>
                  <div className="text-xs text-slate-500">Average return</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <div className="flex items-center space-x-3 mb-6">
                  <MapPinIcon className="w-6 h-6 text-blue-600" />
                  <h3 className="text-2xl font-display font-bold text-slate-900">Free Local Market Analysis</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Get a comprehensive analysis of your local market opportunities and 
                  receive a customized lead generation strategy from our experts.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Local competitor analysis</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Keyword opportunity report</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700">Custom lead generation strategy</span>
                  </div>
                </div>
                
                <button 
                  onClick={handleGetAudit}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Get Your Free Analysis
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Comprehensive Local Lead Generation
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our multi-channel approach ensures your business captures leads from every local touchpoint
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <div className="text-blue-600">{feature.icon}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckIcon className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-display font-bold text-slate-900 mb-4">
              Our 4-Step Process
            </h2>
            <p className="text-xl text-slate-600">
              Proven methodology for generating qualified local leads
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">{step.step}</span>
                </div>
                <h3 className="text-xl font-display font-bold text-slate-900 mb-4">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 bg-green-100 rounded-full px-4 py-2 mb-6">
                  <StarIcon className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-semibold text-sm">Success Story</span>
                </div>
                
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6">
                  {caseStudy.client}
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Challenge</h4>
                    <p className="text-slate-600">{caseStudy.challenge}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Solution</h4>
                    <p className="text-slate-600">{caseStudy.solution}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-4">Results</h4>
                    <div className="space-y-3">
                      {caseStudy.results.map((result, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <CheckIcon className="w-5 h-5 text-green-500" />
                          <span className="text-slate-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-display font-bold text-slate-900 mb-6">Ready to Get Started?</h3>
                <p className="text-slate-600 mb-6">
                  Join hundreds of local businesses that have transformed their lead generation with our proven strategies.
                </p>
                
                <div className="space-y-4">
                  <Link 
                    to="/consultation"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center block"
                  >
                    Schedule Free Consultation
                  </Link>
                  <Link 
                    to="/case-studies"
                    className="w-full bg-white hover:bg-gray-50 text-slate-900 font-semibold py-3 px-6 rounded-lg border-2 border-slate-200 transition-colors text-center block"
                  >
                    Download Case Study
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900">
        <div className="container-custom text-center">
          <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mb-6">
            Ready to Generate More Local Leads?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help your business attract more qualified local customers and grow your revenue.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleCallPhone}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <PhoneIcon className="w-5 h-5" />
              <span>Call (+91) 9711834001</span>
            </button>
            <button 
              onClick={handleSendEmail}
              className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <EnvelopeIcon className="w-5 h-5" />
              <span>Send Email</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LeadGenerationPage;
